// @flow

import React from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { Heading, Hint, Link, MediaQuery, VerticalMenu, DropdownButton } from '@riseart/common';
import {
  ArtActionCheckout,
  ArtOverlayHeader,
  ArtActionRow,
  ArtAction,
  ArtActionDetails,
  ViewInRoomButton,
  ArtActionEnquire,
} from '@riseart/art';
import { application as CONFIG_APP, components as CONFIG_COMPONENTS } from 'Config';
import { art as ENUM_ART } from 'Enum';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { EnquireOverlay } from 'shared_components/enquire/Overlay';
import { EnquireArtworkForm } from 'shared_components/forms/enquire/Artwork';
import { AddCartItemButton } from 'shared_data/providers/queries/AddCartItem';
import { WithMaxWordLength } from 'shared_hocs/common/WithMaxWordLength';
import { Picture } from 'shared_components/common/artdirection/picture/Picture';
import { FavoritesToggleMutation } from 'shared_data/providers/queries/FavoritesToggleMutation';
import {
  formatDimensionsLabel,
  renderViewInRoomPicture,
} from 'shared_services/riseart/utils/artDataUtils';
import { formatCurrency } from 'shared_services/riseart/utils/Utils';

import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';
import {
  artistBioNameCls,
  artistBioNameLongCls,
  artistBioNameVeryLongCls,
  artistBioNameExtraLongCls,
} from 'shared_components/artist/bio/Bio.less';
import {
  artistArtDetailsCls,
  artistArtDetailsLeftCls,
  artistArtDetailsRightCls,
  artistArtDetailsItemPositionCls,
  artistArtDetailsStatusCls,
  artistArtDetailsPriceCls,
  artistArtDetailsActionsCls,
} from 'shared_components/artist/sections/art/details/Details.less';
import {
  artLinkSeparatorCls,
  artLinkDropdownWishlistCls,
} from 'shared_components/arts/detail/Detail.less';

type Props = {
  storeCode: string,
  locale: Object,
  unitSystem: string,
  artistName: string,
  artistUrl: ?string,
  menuItems: Array<Object>,
  art: Object,
  positionLabel: Node,
  children: Node,
};

/**
 * ArtistArtDetails
 *
 * @param {Props} props
 */
export const ArtistArtDetails = ({
  storeCode,
  locale,
  unitSystem = CONFIG_APP.units.defaultSystem,
  artistName,
  artistUrl,
  menuItems,
  children,
  art,
  positionLabel,
}: Props) => {
  if (!art) {
    return null;
  }

  const lengthUnit = CONFIG_APP.units[unitSystem].length;
  const showEnquireButton =
    !art.isProduct || (!art.canBuy && !art.canRent && (art.isEnquire || art.isRented));

  return (
    <div className={artistArtDetailsCls}>
      <div className={artistArtDetailsRightCls}>{children}</div>
      <div className={artistArtDetailsLeftCls}>
        <MediaQuery minWidth={raScreenLg}>
          <React.Fragment>
            <WithMaxWordLength text={artistName}>
              {(maxWordLength) => (
                <Heading
                  tag="h1"
                  level="1"
                  className={classNames(
                    artistBioNameCls,
                    maxWordLength >= 10 && maxWordLength < 13 && artistBioNameLongCls,
                    maxWordLength >= 13 && maxWordLength <= 15 && artistBioNameVeryLongCls,
                    maxWordLength > 15 && artistBioNameExtraLongCls,
                  )}
                >
                  {artistUrl ? (
                    <Link to={artistUrl} title={artistName}>
                      {artistName}
                    </Link>
                  ) : (
                    artistName
                  )}
                </Heading>
              )}
            </WithMaxWordLength>
            <VerticalMenu disableFixedMenu items={menuItems} />
          </React.Fragment>
        </MediaQuery>
        <div>
          <div className={artistArtDetailsItemPositionCls}>{positionLabel}</div>
          <ArtAction>
            <ArtActionDetails
              title={
                <Link to={UrlAssembler.artDetail(art.id, art.slug)} title={art.title} type="light">
                  {art.title}
                </Link>
              }
              dimensions={formatDimensionsLabel([art.height, art.width, art.depth], art.unit, {
                convertTo: lengthUnit.unit,
                precision:
                  typeof lengthUnit.precision.high !== 'undefined' ? lengthUnit.precision.high : 1,
              })}
              additionalText={
                art.skuPrimaryEditionSize ? (
                  <FormattedMessage
                    id="components.art.edition_of"
                    values={{ editionSize: art.skuPrimaryEditionSize }}
                  >
                    {(editionOf: string) => editionOf}
                  </FormattedMessage>
                ) : (
                  <FormattedMessage id={`filters.art.medium.${art.medium}`}>
                    {(medium: string) => medium}
                  </FormattedMessage>
                )
              }
              hint={Hint}
              links={
                <React.Fragment>
                  {art.isEnquire || art.canBuy ? null : (
                    <React.Fragment>
                      <FavoritesToggleMutation artId={art.id} type="semitransparent" />
                      <span className={artLinkSeparatorCls}>|</span>
                    </React.Fragment>
                  )}
                  <FormattedMessage
                    id="components.art.artwork_by_artist"
                    values={{ title: art.title, artist: artistName }}
                  >
                    {(alt: string) => (
                      <ViewInRoomButton
                        scaled
                        previewConfig={CONFIG_COMPONENTS.art.viewInRoom.backgrounds.main}
                        imageWidth={art.width}
                        imageHeight={art.height}
                        previewComponent={
                          !art.is3D &&
                          renderViewInRoomPicture(
                            {
                              images: art.images,
                              width: art.width,
                              height: art.height,
                              alt,
                            },
                            ENUM_ART.image.type.TYPE_FLAT_MASTER,
                          )
                        }
                        overlayHeader={() => (
                          <ArtOverlayHeader
                            productCanBuy={art.canBuy}
                            title={art.title}
                            artistUrl={artistUrl}
                            artistName={artistName}
                            storeCode={storeCode}
                            translations={{ by: <FormattedMessage id="components.art.by" /> }}
                          />
                        )}
                        backgroundPicture={({ config, imgProps }) => (
                          <FormattedMessage id="components.art.viewInRoomBkgAlt">
                            {(alt: string) => (
                              <Picture
                                type="viewInRoom.scaled"
                                artDirectionKey="inRoomBackground.scaled"
                                image={config.background}
                                alt={alt}
                                imgProps={{ ...imgProps }}
                              />
                            )}
                          </FormattedMessage>
                        )}
                      />
                    )}
                  </FormattedMessage>
                </React.Fragment>
              }
              translations={{
                by: <FormattedMessage id="components.art.by" />,
                artworkSizeTooltipHeading: (
                  <FormattedMessage id="components.art.size_tooltip_heading" />
                ),
                artworkSizeTooltipDescription: (
                  <FormattedMessage id="components.art.size_tooltip_description" />
                ),
              }}
            />
          </ArtAction>
          {art.canBuy && art.skuBuyPrice ? (
            <div className={artistArtDetailsPriceCls}>
              {formatCurrency(art.skuBuyPrice, art.storeCode, undefined, {
                locale: locale && locale.name,
                hasSpaceBetweenCurrency: true,
              })}
            </div>
          ) : null}
          {art.isEnquire || art.isSoldOut ? (
            <div className={artistArtDetailsStatusCls}>
              {art.isEnquire ? (
                <FormattedMessage id="components.art.is_enquire">
                  {(text: string) => <div>{text}</div>}
                </FormattedMessage>
              ) : null}
              {art.isSoldOut ? (
                <FormattedMessage id="components.art.sold_out">
                  {(text: string) => <div>{text}</div>}
                </FormattedMessage>
              ) : null}
            </div>
          ) : null}
          <ArtAction className={artistArtDetailsActionsCls}>
            {art.canBuy ? (
              <ArtActionRow>
                <DropdownButton
                  mainButton={
                    <FormattedMessage id="components.art.add_to_basket">
                      {(btnText: string) => (
                        <AddCartItemButton cartId="buy" qty={1} skuId={art.skuBuyId}>
                          {({ loading, onClick }) => (
                            <ArtActionCheckout
                              onClick={onClick}
                              loading={loading}
                              buttonText={btnText}
                            />
                          )}
                        </AddCartItemButton>
                      )}
                    </FormattedMessage>
                  }
                  secondaryButton={
                    showEnquireButton ? null : (
                      <FavoritesToggleMutation
                        artId={art.id}
                        type="primary"
                        size="medium"
                        animated={false}
                        className={artLinkDropdownWishlistCls}
                      />
                    )
                  }
                />
              </ArtActionRow>
            ) : null}
            {showEnquireButton ? (
              <ArtActionRow>
                <DropdownButton
                  mainButton={
                    <EnquireOverlay
                      title={
                        <FormattedMessage id="forms.emailEnquire.overlayTitle">
                          {(text: string) => text}
                        </FormattedMessage>
                      }
                      component={({ onClose }) => (
                        <EnquireArtworkForm
                          onClose={onClose}
                          artwork={art}
                          selectedFrame={null}
                          trigger={ENUM_ART.enquire.trigger.TRIGGER_ENQUIRE_BUTTON}
                        />
                      )}
                    >
                      <ArtActionEnquire type="secondary" width="container">
                        <FormattedMessage id="common.enquire" />
                      </ArtActionEnquire>
                    </EnquireOverlay>
                  }
                  secondaryButton={
                    <FavoritesToggleMutation
                      artId={art.id}
                      type="secondary"
                      size="medium"
                      animated={false}
                      className={artLinkDropdownWishlistCls}
                    />
                  }
                />
              </ArtActionRow>
            ) : null}
          </ArtAction>
        </div>
      </div>
    </div>
  );
};
